/**
 * 水平スクロール
 *
 * .ist-js__horizontalScroll に対して適用される
 *
 * <div class="ist-js__horizontalScroll">
 *   <div class="ist-js__horizontalScrollSticky">
 *     <div class="ist-js__horizontalScrollContent">
 *       <div class="ist-js__horizontalScrollContentInner">
 *         <!-- コンテンツ -->
 *       </div>
 *     </div>
 *   </div>
 * </div>
 */

class HorizontalScroll {
  private headerHeight: number = 0;
  private viewportHeight: number = 0;
  private readonly DEFAULT_SCROLL_WIDTH: number = 660;
  private readonly CLASS_NAME: string = '.ist-js__horizontalScroll';
  private readonly CONTENT_INNER_CLASS_NAME: string =
    '.ist-js__horizontalScrollContentInner';
  private scrollRaf: number | null = null;
  private resizeRaf: number | null = null;

  /**
   * コンストラクタ
   */
  constructor() {
    if (document.readyState === 'loading') {
      window.addEventListener('load', () => this.init());
    } else {
      this.init();
    }
  }

  /**
   * 初期化
   */
  private init(): void {
    window.addEventListener('resize', this.handleResize.bind(this), {
      passive: true,
    });
    window.addEventListener('scroll', this.handleScroll.bind(this), {
      passive: true,
    });

    setTimeout(() => {
      this.setInfo();
      this.setTransform();
    }, 0);
  }

  /**
   * リサイズハンドラー
   */
  private handleResize(): void {
    if (this.resizeRaf) {
      cancelAnimationFrame(this.resizeRaf);
    }

    this.resizeRaf = requestAnimationFrame(() => {
      this.setInfo();
      this.setTransform();
      this.resizeRaf = null;
    });
  }

  /**
   * スクロールハンドラー
   */
  private handleScroll(): void {
    if (this.scrollRaf) {
      cancelAnimationFrame(this.scrollRaf);
    }

    this.scrollRaf = requestAnimationFrame(() => {
      this.setTransform();
      this.scrollRaf = null;
    });
  }

  /**
   * 破棄
   */
  public destroy(): void {
    if (this.scrollRaf) {
      cancelAnimationFrame(this.scrollRaf);
    }
    if (this.resizeRaf) {
      cancelAnimationFrame(this.resizeRaf);
    }

    window.removeEventListener('resize', this.handleResize.bind(this));
    window.removeEventListener('scroll', this.handleScroll.bind(this));
  }

  /**
   * 情報更新
   */
  private setInfo(): void {
    const elements = document.querySelectorAll<HTMLElement>(this.CLASS_NAME);
    elements.forEach((element) => {
      const horizontalScrollElement = element;
      const contentInner = element.querySelector<HTMLElement>(
        this.CONTENT_INNER_CLASS_NAME
      ) as HTMLElement;
      const minScale =
        document.documentElement.clientWidth / contentInner.scrollWidth;

      let scale: number = 0;
      let contentHeight: number = 0;

      this.headerHeight =
        document.querySelector('.ist-header')?.clientHeight || 0;
      this.viewportHeight = window.innerHeight;
      scale =
        (this.viewportHeight - this.headerHeight) / this.DEFAULT_SCROLL_WIDTH <
        1
          ? (this.viewportHeight - this.headerHeight) /
            this.DEFAULT_SCROLL_WIDTH
          : 1;

      // 最小スケールを適用
      if (minScale > scale) {
        scale = minScale;
      }

      // scale
      element.style.setProperty('--horizontalScrollScale', scale.toString());

      // stickyTop
      element.style.setProperty('--horizontalScrollStickyTop', '0px');

      if (horizontalScrollElement && contentInner) {
        // contentHeightを修正
        contentHeight =
          (contentInner.scrollWidth * scale -
            document.documentElement.clientWidth) *
            2 +
          contentInner.clientHeight * scale +
          1500;

        // コンテナの高さを設定
        element.style.setProperty(
          '--horizontalScrollContentHeight',
          `${contentHeight}px`
        );
      }
    });
  }

  /**
   * ハイブリットスクロール
   * スクロールに合わせてコンテンツにtransformを適用する
   */
  private setTransform(): void {
    const elements = document.querySelectorAll<HTMLElement>(this.CLASS_NAME);
    elements.forEach((element) => {
      const moveContent = element.querySelector<HTMLElement>(
        this.CONTENT_INNER_CLASS_NAME
      );
      const scale = parseFloat(
        element.style.getPropertyValue('--horizontalScrollScale')
      );
      const contentHeight = parseFloat(
        element.style.getPropertyValue('--horizontalScrollContentHeight')
      );

      if (moveContent && scale && contentHeight) {
        const startPosition = element.offsetTop;
        const firstItem = element.querySelector<HTMLElement>(
          '.ist-homeInnovationScroll__item--large'
        );
        const target = element.querySelector<HTMLElement>(
          '.ist-js__horizontalScrollTarget'
        );

        if (target && firstItem) {
          const firstItemWidth = firstItem.offsetWidth;
          const targetWidth = target.scrollWidth;
          const visibleWidth = document.documentElement.clientWidth / scale;
          const totalWidth = firstItemWidth + targetWidth;
          const requiredScroll = totalWidth - visibleWidth;
          const scrollRange =
            contentHeight - moveContent.clientHeight * scale - 1000; // 後ろの500pxを除外
          const currentScroll =
            (window.scrollY || window.pageYOffset) - startPosition;

          // 前後の500pxオフセットを設定
          const scrollOffset = 500;
          const adjustedScroll = Math.max(0, currentScroll - scrollOffset);

          if (currentScroll >= scrollOffset && currentScroll <= scrollRange) {
            // 有効なスクロール範囲内での進捗を計算
            const scrollProgress = Math.min(
              adjustedScroll / (scrollRange - scrollOffset),
              1
            );
            const translateX = -(requiredScroll * scrollProgress);

            target.style.willChange = 'transform';
            target.style.transform = `translate3d(${translateX}px, 0, 0)`;
          } else if (currentScroll < scrollOffset) {
            // 開始前の500px
            target.style.transform = 'translate3d(0, 0, 0)';
            target.style.willChange = 'auto';
          } else if (currentScroll > scrollRange) {
            // 完了後の500px
            target.style.transform = `translate3d(${-requiredScroll}px, 0, 0)`;
            target.style.willChange = 'auto';
          }
        }
      }
    });
  }
}

export default HorizontalScroll;
